import get from 'lodash/get';

import { transformNamedAnswers } from './calendarCommon';
import { ICLOSED_EVENT_SCHEDULED, ICLOSED_URL_REGEX, NAMED_ANSWERS } from './constants';

import type { FormValues, Input } from '$lib/types/Form';

// some commonly asked by users custom fields
const ICLOSED_PREFILL_KEYS = ['company', 'notes', 'message', 'description'];

/**
 * iClosed integration module
 * Provides utilities for validating URLs, building iframe URLs with prefill data and
 * UTM parameters, and detecting booking confirmation events
 */
export const iclosed = {
    validateUrl: (url: string): boolean => {
        return ICLOSED_URL_REGEX.test(url);
    },

    /**
     * Extracts named answers (name, email, phone) plus common custom-field keys
     * (company, notes, message, description) that users typically configure as
     * custom fields in iClosed. firstName/lastName are normalised into a single name.
     */
    extractNamedAnswers: (inputs: Input[], formData: FormValues): FormValues => {
        const namedAnswers: FormValues = {};

        inputs.forEach((input) => {
            const { name } = input;
            const value = get(formData, name, '');

            if (NAMED_ANSWERS.includes(name)) {
                namedAnswers[name] = value;
            } else if (ICLOSED_PREFILL_KEYS.includes(name)) {
                namedAnswers[name] = value;
            }
        });

        return transformNamedAnswers(namedAnswers);
    },

    /**
     * Builds the iframe URL with prefill data and UTM parameters as query strings
     */
    buildIframeUrl: (
        baseUrl: string,
        namedAnswers: FormValues,
        trackingProperties: FormValues,
    ): string => {
        const url = new URL(baseUrl);
        const { firstName, lastName, name: directName, email, phone } = namedAnswers;

        // Combine firstName/lastName into name when no direct name field is present
        let finalName = '';

        if (directName) {
            finalName = directName as string;
        } else if (firstName && lastName) {
            finalName = `${firstName} ${lastName}`;
        } else if (firstName) {
            finalName = firstName as string;
        } else if (lastName) {
            finalName = lastName as string;
        }

        // iClosed expects the `iclosed`-prefixed keys for built-in contact fields
        // See: https://docs.iclosed.io/en/articles/9830929-how-to-prefill-iclosed-form-contact-information-and-answers
        if (finalName) url.searchParams.set('iclosedName', finalName);

        if (email) url.searchParams.set('iclosedEmail', email as string);

        if (phone) url.searchParams.set('iclosedPhone', phone as string);

        ICLOSED_PREFILL_KEYS.forEach((key) => {
            const value = namedAnswers[key];

            if (value) {
                url.searchParams.set(key, value as string);
            }
        });

        const { utm_campaign, utm_source, utm_medium, utm_content, utm_term } = trackingProperties;

        if (utm_source) url.searchParams.set('utm_source', utm_source as string);

        if (utm_medium) url.searchParams.set('utm_medium', utm_medium as string);

        if (utm_campaign) url.searchParams.set('utm_campaign', utm_campaign as string);

        if (utm_content) url.searchParams.set('utm_content', utm_content as string);

        if (utm_term) url.searchParams.set('utm_term', utm_term as string);

        return url.toString();
    },

    /**
     * iClosed posts events shaped like `{ type: 'iclosed.call_scheduled' }`
     * (also `iclosed.potential`, `iclosed.qualified`, `iclosed.disqualified`).
     */
    isBookingSuccessful: (data: { type: string }): boolean => {
        return get(data, 'type', '') === ICLOSED_EVENT_SCHEDULED;
    },
};
